import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useParams } from "react-router-dom";
import classnames from "classnames";

import {
    Button,
    Card,
    CardBody,
    Container,
    Modal,
    ModalBody,
    Nav,
    NavItem,
    NavLink,
    Table,
    UncontrolledTooltip,
} from "reactstrap";
import { toast } from "react-toastify";
import { urlEndPoint } from "helpers/GeneralHelper";
import AchievementCustomTable from "components/Common/AchievementCustomTable";
import {
    fetchIncentiveMonthlyThreeTableAndFilterSchema,
    getIncentiveMonthlyThreeCandidates,
    getIncentiveMonthlyThreeDetails,
} from "model/IncentiveMonthlyModel";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";

const defaultDetailColumns = [
    { Header: "Client", accessor: "clientName" },
    { Header: "Target Min", accessor: "targetMin" },
    { Header: "Target Max", accessor: "targetMax" },
    { Header: "Rs/Per", accessor: "perAmount" },
    { Header: "Negative", accessor: "isNegative" },
    { Header: "Achieve", accessor: "achieve" },
    { Header: "Work Days", accessor: "workDays" },
    { Header: "Count", accessor: "incentiveCount" },
    { Header: "Incentive", accessor: "incentiveAmount" },
    { Header: "Notes", accessor: "notes", type: "text" },
    { Header: "Hired", accessor: "hiredCount" },
    { Header: "H. Incentive", accessor: "hiredAmount" },
    { Header: "Total", accessor: "totalAmount" },
];

const TAB_ORDER = ["recruiter", "backend"];

const normalizeIncentiveTab = (value) => {
    if (value == null) return "";
    const tab = String(value).trim().toLowerCase();
    return tab || "";
};

const formatTabLabel = (tabKey = "") =>
    String(tabKey)
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

const RTR_ACKNOWLEDGE_PREFILL_STORAGE_KEY = "rtrAcknowledgePrefillFilters";

const buildCandidateDisplayName = (item = {}) =>
    `${item?.firstName || ""} ${item?.lastName || ""}`.trim();

const RTR_ACKNOWLEDGE_NOTE_VALUES = new Set([
    "rtr yes",
    "in progress",
    "complete",
]);

const normalizeCandidateNote = (item = {}) =>
    String(item?.note ?? item?.notes ?? "").trim().toLowerCase();

const shouldShowRtrAcknowledgementAction = (item = {}) =>
    RTR_ACKNOWLEDGE_NOTE_VALUES.has(normalizeCandidateNote(item));

const sortTabKeys = (keys = []) => {
    const unique = [...new Set(keys.filter(Boolean))];
    return unique.sort((a, b) => {
        const aIndex = TAB_ORDER.indexOf(a);
        const bIndex = TAB_ORDER.indexOf(b);
        if (aIndex !== -1 || bIndex !== -1) {
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        }
        return a.localeCompare(b);
    });
};

const MonthlyDetailsThree = () => {
    const { id } = useParams();
    const { fetchMetadataSchema } = useCommonFilterOptions();
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [tabDataMap, setTabDataMap] = useState({});
    const [availableTabs, setAvailableTabs] = useState([]);
    const [activeTab, setActiveTab] = useState("");
    const [tableMetaData, setTableMetaData] = useState([]);
    const [candidateModalOpen, setCandidateModalOpen] = useState(false);
    const [candidateModalTitle, setCandidateModalTitle] = useState("");
    const [candidateList, setCandidateList] = useState([]);
    const [candidateLoading, setCandidateLoading] = useState(false);

    const toggleCandidateModal = () => setCandidateModalOpen((prev) => !prev);

    const handleOpenCallDetails = useCallback((item = {}) => {
        const candidateClientId = String(item?.candidateClientId ?? item?.id ?? "").trim();
        if (!candidateClientId) {
            toast.error("Candidate id is missing");
            return;
        }
        window.open(
            `/${urlEndPoint.callDetails}/${candidateClientId}`,
            "_blank",
            "noopener,noreferrer"
        );
    }, []);

    const handleOpenRtrAcknowledgement = useCallback((item = {}) => {
        const search = buildCandidateDisplayName(item);
        if (!search) {
            toast.error("Candidate name is missing");
            return;
        }
        const params = new URLSearchParams();
        params.set("search", search);
        params.set("acknowledgeType", "yes");
        const prefillFilters = {
            search,
            acknowledgeType: "yes",
            decision: "yes",
        };
        try {
            sessionStorage.setItem(
                RTR_ACKNOWLEDGE_PREFILL_STORAGE_KEY,
                JSON.stringify(prefillFilters)
            );
        } catch {
            // ignore storage failures
        }
        window.open(
            `/${urlEndPoint.rtrAcknowledgement}?${params.toString()}`,
            "_blank",
            "noopener,noreferrer"
        );
    }, []);

    const handleClientClick = async (detail = {}) => {
        const detailsId = detail?.incentiveClientDetailsId || "";
        setCandidateModalTitle(String(detail?.clientName || "").toUpperCase());
        setCandidateList([]);
        setCandidateModalOpen(true);
        if (!detailsId) return;

        setCandidateLoading(true);
        const response = await getIncentiveMonthlyThreeCandidates(detailsId);
        const responseData = response?.data || {};
        const records = Array.isArray(responseData?.data)
            ? responseData?.data
            : Array.isArray(responseData?.records)
                ? responseData?.records
                : Array.isArray(responseData)
                    ? responseData
                    : [];
        setCandidateList(records);
        setCandidateLoading(false);
    };

    const getDetailTableSchema = async () => {
        const response = await fetchMetadataSchema("metadata:incentiveMonthlyThree", () =>
            fetchIncentiveMonthlyThreeTableAndFilterSchema({})
        );
        const columns = response?.data?.detailTable?.columns || [];
        const excludedFields = new Set(["userType", "userTypeName", "incentiveTab", "tab"]);
        const excludedTitles = new Set(["user type", "tab"]);
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns
                    .filter((col) => {
                        const field = String(col?.field || "").trim();
                        const title = String(col?.title || "").trim().toLowerCase();
                        return !excludedFields.has(field) && !excludedTitles.has(title);
                    })
                    .map((col) => ({
                        Header: col?.title,
                        accessor: col?.field,
                        type: col?.type,
                    }))
            );
            return;
        }
        setTableMetaData(defaultDetailColumns);
    };

    const buildRowFromDetail = (detail = {}, index = 0, userId = "") => {
        const columns = tableMetaData?.length ? tableMetaData : defaultDetailColumns;
        const row = {
            id:
                detail?.incentiveClientDetailsId ||
                `${detail?.incentiveMonthDetailsId || userId || "row"}-${index}`,
            // Used by AchievementCustomTable member total calculation
            incentive: Number(detail?.totalAmount ?? detail?.incentiveAmount ?? 0),
        };

        columns.forEach((col) => {
            const field = col?.accessor;
            if (!field) return;

            if (field === "clientName") {
                row.clientName = (
                    <span
                        role="button"
                        tabIndex={0}
                        className="inc-client-link"
                        onClick={() => handleClientClick(detail)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleClientClick(detail);
                            }
                        }}
                    >
                        {detail?.clientName || ""}
                    </span>
                );
                return;
            }

            if (field === "isNegative") {
                row.isNegative = detail?.isNegative ? "Yes" : "No";
                return;
            }

            row[field] = detail?.[field] ?? "";
        });

        return row;
    };

    const buildMembersForRecords = (records = []) =>
        (Array.isArray(records) ? records : [])
            .map((item) => {
                const rows = (Array.isArray(item?.details) ? item.details : []).map((detail, index) =>
                    buildRowFromDetail(detail, index, item?.userId)
                );
                if (!rows.length) return null;
                return {
                    userId: item?.userId || "",
                    name: item?.userName || "User",
                    rows,
                };
            })
            .filter(Boolean);

    const getMonthlyDetails = async () => {
        if (!id) {
            setTabDataMap({});
            setAvailableTabs([]);
            setActiveTab("");
            return;
        }
        setLoading(true);
        const response = await getIncentiveMonthlyThreeDetails(id);
        const responseData = response?.data || {};
        const records = Array.isArray(responseData?.data)
            ? responseData?.data
            : Array.isArray(responseData)
                ? responseData
                : [];

        // Skip users with missing/null incentiveTab; group remaining by tab
        const grouped = {};
        (Array.isArray(records) ? records : []).forEach((item) => {
            const tabKey = normalizeIncentiveTab(item?.incentiveTab);
            if (!tabKey) return;
            if (!grouped[tabKey]) grouped[tabKey] = [];
            grouped[tabKey].push(item);
        });

        const nextTabDataMap = {};
        Object.keys(grouped).forEach((tabKey) => {
            const members = buildMembersForRecords(grouped[tabKey]);
            if (!members.length) return;
            nextTabDataMap[tabKey] = [
                {
                    team: `${formatTabLabel(tabKey)} Incentive`,
                    teamLead: "",
                    members,
                },
            ];
        });

        const tabs = sortTabKeys(Object.keys(nextTabDataMap));
        setTabDataMap(nextTabDataMap);
        setAvailableTabs(tabs);
        setActiveTab((prev) => (tabs.includes(prev) ? prev : tabs[0] || ""));
        setLoading(false);
    };

    useEffect(() => {
        getDetailTableSchema();
    }, []);

    useEffect(() => {
        if (!tableMetaData?.length) return;
        getMonthlyDetails();
    }, [id, tableMetaData]);

    const activeTabData = useMemo(
        () => (activeTab && tabDataMap[activeTab] ? tabDataMap[activeTab] : []),
        [activeTab, tabDataMap]
    );

    // Filtering within the active tab
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return activeTabData;

        return activeTabData
            .map((team) => ({
                ...team,
                members: (Array.isArray(team?.members) ? team.members : [])
                    .map((member) => ({
                        ...member,
                        rows: (Array.isArray(member?.rows) ? member.rows : []).filter((row) =>
                            Object.values(row).some((val) =>
                                String(val).toLowerCase().includes(searchQuery.toLowerCase())
                            )
                        ),
                    }))
                    .filter((member) => member.rows.length > 0),
            }))
            .filter((team) => team.members.length > 0);
    }, [searchQuery, activeTabData]);

    const hasTableRows = useMemo(
        () =>
            (Array.isArray(filteredData) ? filteredData : []).some((team) =>
                (Array.isArray(team?.members) ? team.members : []).some(
                    (member) => Array.isArray(member?.rows) && member.rows.length > 0
                )
            ),
        [filteredData]
    );

    const getTabMemberCount = (tabKey) => {
        const teams = tabDataMap[tabKey] || [];
        return teams.reduce(
            (sum, team) => sum + (Array.isArray(team?.members) ? team.members.length : 0),
            0
        );
    };

    return (
        <React.Fragment>
            <div className="page-content incentive-monthly-details-page">
                <Container fluid>
                    <Card className="inc-details-content-card border-0 shadow-sm">
                        <CardBody className="p-0">
                            {loading || !tableMetaData?.length ? (
                                <div className="p-4">
                                    <Loaders />
                                </div>
                            ) : availableTabs.length ? (
                                <>
                                    <div className="inc-details-tabs-wrap">
                                        <Nav tabs className="inc-details-tabs mb-0">
                                            {availableTabs.map((tabKey) => (
                                                <NavItem key={tabKey}>
                                                    <NavLink
                                                        className={classnames("inc-details-tab", {
                                                            active: activeTab === tabKey,
                                                        })}
                                                        onClick={() => setActiveTab(tabKey)}
                                                    >
                                                        <span className="inc-details-tab-label">
                                                            {formatTabLabel(tabKey)}
                                                        </span>
                                                        <span className="inc-details-tab-count">
                                                            {getTabMemberCount(tabKey)}
                                                        </span>
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    </div>

                                    {hasTableRows ? (
                                        <div className="p-3">
                                            <AchievementCustomTable
                                                className="inc-achievement-table-card"
                                                columns={tableMetaData}
                                                data={filteredData}
                                                isActionRequired={false}
                                                pageName="IncentiveMonthlyDetailsThree"
                                            />
                                        </div>
                                    ) : (
                                        <div className="inc-empty-state p-5 text-center text-muted">
                                            <i className="bx bx-line-chart display-4 d-block mb-2 opacity-25" />
                                            <span className="fw-semibold">
                                                No incentive data for this tab.
                                            </span>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="inc-empty-state p-5 text-center text-muted">
                                    <i className="bx bx-line-chart display-4 d-block mb-2 opacity-25" />
                                    <span className="fw-semibold">No incentive data for this month.</span>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Modal
                        isOpen={candidateModalOpen}
                        toggle={toggleCandidateModal}
                        backdrop="static"
                        className="modal-full-height"
                        centered
                        size="xl"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="fw-bold mb-0">{candidateModalTitle || "CANDIDATES"}</h5>
                                <Button close onClick={toggleCandidateModal} />
                            </div>
                            <hr />
                            <div className="table-responsive">
                                <Table bordered hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Candidate</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Amount (Rs.)</th>
                                            <th>Note</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!candidateLoading && candidateList?.length ? (
                                            candidateList.map((item, idx) => {
                                                const rowKey =
                                                    item?.id || item?.candidateId || idx;
                                                const showRtrAcknowledgementAction =
                                                    shouldShowRtrAcknowledgementAction(item);
                                                return (
                                                    <tr key={rowKey}>
                                                        <td>
                                                            {(
                                                                buildCandidateDisplayName(item) ||
                                                                item?.candidate ||
                                                                item?.candidateName ||
                                                                item?.name ||
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td>{item?.email || "-"}</td>
                                                        <td>{item?.phoneNumber || item?.phone || "-"}</td>
                                                        <td>{item?.amount ?? "-"}</td>
                                                        <td>{item?.note || item?.notes || "-"}</td>
                                                        <td className="text-center align-middle">
                                                            <div className="d-inline-flex align-items-center justify-content-center gap-1">

                                                                {/* Call Details */}
                                                                <span
                                                                    id={`call-details-${item.id}`}
                                                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                    onClick={() => handleOpenCallDetails(item)}
                                                                    role="button"
                                                                    aria-label="Call details"
                                                                >
                                                                    <i
                                                                        className="mdi mdi-phone-log text-success"
                                                                        aria-hidden="true"
                                                                    />
                                                                </span>

                                                                {/* <UncontrolledTooltip
                                                                    target={`call-details-${item.id}`}
                                                                    placement="top"
                                                                    className="action-tooltip-over-modal"
                                                                >
                                                                    Call Details
                                                                </UncontrolledTooltip> */}


                                                                {/* RTR Acknowledgement */}
                                                                {showRtrAcknowledgementAction ? (
                                                                    <>
                                                                        <span
                                                                            id={`rtr-acknowledgement-${item.id}`}
                                                                            className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                            onClick={() => handleOpenRtrAcknowledgement(item)}
                                                                            role="button"
                                                                            aria-label="RTR acknowledgement"
                                                                        >
                                                                            <i
                                                                                className="mdi mdi-clipboard-check-outline text-success"
                                                                                aria-hidden="true"
                                                                            />
                                                                        </span>

                                                                        {/* <UncontrolledTooltip
                                                                            target={`rtr-acknowledgement-${item.id}`}
                                                                            placement="top"
                                                                            className="action-tooltip-over-modal"
                                                                        >
                                                                            RTR Acknowledgement
                                                                        </UncontrolledTooltip> */}
                                                                    </>
                                                                ) : null}

                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : !candidateLoading ? (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted">
                                                    No data found
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted">
                                                    Loading...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </ModalBody>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

MonthlyDetailsThree.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(MonthlyDetailsThree);
